import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { InvalidTokenError, jwtDecode } from 'jwt-decode';

import { useChangePassword } from '../../api-calls/auth.queries';
import { routes } from '../../constants';
import { register as registerValidation } from '../../validation/schemas';
import Button from '../../components/Button';
import * as T from '../../components/Typography';
import Error from '../../components/Error';
import colors from '../../theme/colors';
import logoWhiteBg from '../../components/assets/logo-navy.png';
import InvalidToken from '../../components/InvalidToken';
import { ControllerTextField } from '../../components/Inputs/Textfield';

interface ChangePasswordFormInputs {
  password: string;
  confirmPassword: string;
}

interface DecodedToken {
  userId: string;
  purpose: string;
  exp: number;
}

const ChangePassword: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<ChangePasswordFormInputs>({
    resolver: yupResolver(registerValidation)
  });

  const navigate = useNavigate();

  const { mutateAsync, isPending } = useChangePassword();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jwtToken = urlParams.get('token');

    if (jwtToken) {
      try {
        const decodedToken: DecodedToken = jwtDecode(jwtToken);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp > currentTime && decodedToken.userId) {
          setToken(jwtToken);
        }
      } catch (error) {
        if (error instanceof InvalidTokenError) {
          console.error('Invalid token provided:', error);
        } else {
          console.error('Unexpected error occurred:', error);
        }
      }
    }
  }, []);

  const onSubmit = async (data: ChangePasswordFormInputs) => {
    const { password } = data;

    try {
      await mutateAsync({ token, password });
      navigate(routes.CHANGE_PASSWORD_SUCCESS);
    } catch (err) {
      setError('Failed to change password.');
    }
  };

  if (error) {
    return <Error />;
  }

  if (!token) {
    return (
      <InvalidToken
        title={'Your password reset link has expired'}
        subtitle={
          'The password reset link you clicked is no longer valid.Please request a new link to reset your password and secure your account.'
        }
        btnLabel={'Request new reset link'}
        onBtnClick={() => navigate(routes.RESET_PASSWORD)}
      />
    );
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid
        container
        maxWidth={'384px'}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <img src={logoWhiteBg} alt="account verified" width="118" height="48" />
        <T.H4
          mt="6"
          mb="2"
          ta="center"
          color={colors.neutral.shades.shade700}
        >
          SET YOUR NEW PASSWORD
        </T.H4>
        <T.TextSmallRegular
          ta="center"
          mb={6}
          center
          color={colors.neutral.shades.shade600}
        >
          Create a strong password to secure your account.
        </T.TextSmallRegular>
      </Grid>
      <form onSubmit={handleSubmit(onSubmit)} style={{width: '100%', maxWidth: '384px'}}>
        <Grid item>
          <ControllerTextField
            control={control}
            label="New password"
            name="password"
            placeholder="Enter a new password"
            type="password"
          />
        </Grid>
        <Grid item mt={2}>
          <ControllerTextField
            control={control}
            label="Confirm new password"
            name="confirmPassword"
            placeholder="Confirm new Password"
            type="password"
          />
        </Grid>

        <Grid item mt={2}>
          <Button
            type="submit"
            variant="blue"
            loading={isPending}
            disabled={isPending}
            mt={5}
          >
            {isPending ? 'Changing Password...' : 'Change Password'}
          </Button>
        </Grid>
      </form>
      <Box>
        <T.TextSmallRegular
          ta="center"
          mt="4"
          color={colors.neutral.shades.shade500}
        >
          Remembered your password?{' '}
          <T.Link
            to={routes.LOGIN}
            decoration="none"
            color={colors.blue.shades.shade700}
          >
            Log in here
          </T.Link>
        </T.TextSmallRegular>
      </Box>
    </Grid>
  );
};

export default ChangePassword;
